import * as Sentry from '@sentry/react';
import { env } from '@/config/env';

// Keep browser telemetry payloads privacy-safe by redacting known sensitive fields
// and truncating large string values before they leave the client.
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,}/gi;
const MAX_LENGTH = 512;
const EXTENSION_PROTOCOLS = ['chrome-extension://', 'moz-extension://'];

const REDACT_KEYS = [
  'email',
  'subject',
  'body',
  'html',
  'payload',
];

function shouldRedactKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return REDACT_KEYS.some((needle) => normalized.includes(needle));
}

function isProductionTelemetryEnvironment(): boolean {
  const label = (
    env.VITE_SENTRY_ENVIRONMENT ??
    env.VITE_ENVIRONMENT ??
    env.VITE_APP_ENV ??
    ''
  ).toLowerCase();

  return Boolean(import.meta.env?.PROD) || label === 'production';
}

function shouldLogSensitivePayloads(): boolean {
  return Boolean(env.VITE_LOG_SENSITIVE_PAYLOADS) && !isProductionTelemetryEnvironment();
}

function redactString(value: string, key = ''): string {
  const sensitiveKey = shouldRedactKey(key);
  if (sensitiveKey && !shouldLogSensitivePayloads()) {
    return '[REDACTED]';
  }

  const withoutEmails = sensitiveKey ? value : value.replace(EMAIL_REGEX, '[EMAIL]');
  if (withoutEmails.length <= MAX_LENGTH) return withoutEmails;
  return `${withoutEmails.slice(0, MAX_LENGTH)}...[truncated]`;
}

function sanitizeValue(value: unknown, key = '', depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (depth > 4) return '[DEPTH_LIMIT]';

  if (typeof value === 'string') {
    return redactString(value, key);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, key, depth + 1));
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [childKey, childValue] of Object.entries(value as Record<string, unknown>)) {
      out[childKey] = sanitizeValue(childValue, childKey, depth + 1);
    }
    return out;
  }

  return String(value);
}

function collectNoiseSignals(event: Sentry.Event, hint?: Sentry.EventHint): string[] {
  const signals: string[] = [];

  if (event.message) signals.push(event.message);

  for (const exception of event.exception?.values ?? []) {
    if (exception.type) signals.push(exception.type);
    if (exception.value) signals.push(exception.value);

    for (const frame of exception.stacktrace?.frames ?? []) {
      if (frame.filename) signals.push(frame.filename);
      if (frame.abs_path) signals.push(frame.abs_path);
    }
  }

  const originalException = hint?.originalException;
  if (typeof originalException === 'string') {
    signals.push(originalException);
  } else if (originalException && typeof originalException === 'object') {
    const errorLike = originalException as { message?: unknown; stack?: unknown };
    if (typeof errorLike.message === 'string') signals.push(errorLike.message);
    if (typeof errorLike.stack === 'string') signals.push(errorLike.stack);
  }

  return signals;
}

function shouldDropBrowserNoise(event: Sentry.Event, hint?: Sentry.EventHint): boolean {
  const signals = collectNoiseSignals(event, hint);

  if (signals.some((signal) => EXTENSION_PROTOCOLS.some((protocol) => signal.includes(protocol)))) {
    return true;
  }

  return signals.some((signal) => signal.includes('ResizeObserver loop'));
}

function fnv1aHash(input: string): string {
  // Fast non-cryptographic hash for correlation/diagnostic fingerprints only.
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function resolveSentryEnvironment(): {
  environment: string;
  usedFallback: boolean;
} {
  const configuredEnvironment =
    env.VITE_SENTRY_ENVIRONMENT ??
    env.VITE_ENVIRONMENT ??
    env.VITE_APP_ENV;

  return {
    environment: configuredEnvironment ?? 'development',
    usedFallback: !configuredEnvironment,
  };
}

function shouldSuppressDevelopmentEvents(
  environment: string,
  usedFallback: boolean
): boolean {
  return environment === 'development' && !usedFallback && !import.meta.env?.PROD;
}

function resolveRelease(): string | undefined {
  return (
    import.meta.env?.VITE_SENTRY_RELEASE ||
    import.meta.env?.VITE_VERCEL_GIT_COMMIT_SHA ||
    undefined
  );
}

function resolveTracesSampleRate(): number {
  // Explicit env override always wins.
  if (env.VITE_SENTRY_TRACES_SAMPLE_RATE != null) {
    return env.VITE_SENTRY_TRACES_SAMPLE_RATE;
  }
  // Environment-aware defaults: staging 0.25, production 0.1.
  // Staging was 1.0 but that 5x'd our quota with little debugging benefit —
  // errors still auto-sample regardless of this rate.
  const { environment } = resolveSentryEnvironment();
  return environment === 'production' ? 0.1 : 0.25;
}

export function initSentry(): void {
  if (!env.VITE_SENTRY_DSN) {
    // Opt-in by configuration so local builds can run without telemetry noise.
    return;
  }

  const tracesSampleRate = resolveTracesSampleRate();
  const replaysSessionSampleRate = env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.05;
  const replaysOnErrorSampleRate = env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE ?? 1.0;
  const release = resolveRelease();
  const { environment, usedFallback } = resolveSentryEnvironment();

  if (environment === 'development' && usedFallback) {
    console.warn(
      '[Sentry] environment resolved to "development" with no explicit environment variable. ' +
      'Events will be suppressed until VITE_SENTRY_ENVIRONMENT, VITE_ENVIRONMENT, or VITE_APP_ENV is set.'
    );
  }

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment,
    release,
    tracesSampleRate,
    integrations: [Sentry.replayIntegration()],
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    tracePropagationTargets: [
      'localhost',
      env.VITE_MILESTONE_SERVICE_URL,
      env.VITE_RAILWAY_API_URL,
      /^https:\/\/.*\.railway\.app/,
    ].filter(Boolean),
    beforeSend(event, hint) {
      // Drop events from local development — all 210 existing Sentry events
      // are localhost noise that makes production alerts useless.
      if (shouldSuppressDevelopmentEvents(environment, usedFallback)) return null;

      // Drop known browser-extension + DOM noise that isn't actionable.
      if (shouldDropBrowserNoise(event, hint)) return null;

      // Sanitize the entire outbound event envelope for consistent redaction.
      const sanitized = sanitizeValue(event) as Sentry.Event;
      return sanitized;
    },
  });

  window.addEventListener('unhandledrejection', (event) => {
    capturePortalException(event.reason, { handler: 'unhandledrejection' });
  });

  window.addEventListener('error', (event) => {
    capturePortalException(event.error, { handler: 'window.error' });
  });
}

export function setEmailTelemetryContext(context: {
  transactionId?: number;
  organizationId?: string;
  templateId?: string;
  recipientContactId?: string;
  correlationId?: string;
  idempotencyKey?: string;
  recipientEmail?: string;
  subject?: string;
  body?: string;
}): void {
  const includeSensitivePayloads = shouldLogSensitivePayloads();
  // Store deterministic hashes/previews instead of raw content so correlation
  // is possible without exposing recipient PII or full message bodies.
  const bodyPreview = context.body ? context.body.slice(0, 120) : null;
  const bodyHash = context.body ? fnv1aHash(context.body) : null;
  const subjectHash = context.subject ? fnv1aHash(context.subject) : null;

  Sentry.setContext('email_telemetry', {
    transaction_id: context.transactionId ?? null,
    organization_id: context.organizationId ?? null,
    template_id: context.templateId ?? null,
    recipient_contact_id: context.recipientContactId ?? null,
    correlation_id: context.correlationId ?? null,
    idempotency_key: context.idempotencyKey ?? null,
    recipient_email: includeSensitivePayloads ? context.recipientEmail ?? null : null,
    recipient_email_redacted: context.recipientEmail && !includeSensitivePayloads ? '[REDACTED]' : null,
    subject_hash: subjectHash,
    body_hash: bodyHash,
    subject_preview: includeSensitivePayloads ? context.subject?.slice(0, 120) ?? null : null,
    body_preview_redacted: bodyPreview ? redactString(bodyPreview, 'body_preview') : null,
  });
}

export function capturePortalException(
  error: unknown,
  metadata: Record<string, unknown> = {}
): string {
  return Sentry.captureException(error, {
    extra: sanitizeValue(metadata) as Record<string, unknown>,
  });
}

export function setSentryUser(userId: string, email?: string, orgId?: string): void {
  Sentry.setUser({ id: userId, ...(email && { email }) });
  if (orgId) {
    Sentry.setTag('organization_id', orgId);
  }
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
  Sentry.setTag('organization_id', undefined);
}

export const __sentryTestUtils = {
  sanitizeValue,
  redactString,
  resolveSentryEnvironment,
  shouldLogSensitivePayloads,
  shouldSuppressDevelopmentEvents,
};
